import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Switch,
  IconButton,
  Paper,
  Grid,
  Tooltip
} from '@mui/material';
import { AddPhotoAlternate as AddPhotoAlternateIcon, Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getDealerLogos, addDealerLogo, updateDealerLogo, deleteDealerLogo } from '../services/contentService';

import CloudinaryUploadWidget from './CloudinaryUploadWidget.jsx';

const MAX_LOGOS = 5;

const DealershipLogosEditor = () => {
  const [logos, setLogos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [newLogoName, setNewLogoName] = useState('');
  const [newLogoUrl, setNewLogoUrl] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const fetchLogos = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getDealerLogos();
      const editorLogos = data.map(logo => ({
          ...logo,
          isActive: logo.isEnabled !== undefined ? logo.isEnabled : true // Default to true if isEnabled is missing
        }));
      setLogos(editorLogos || []);
    } catch (err) {
      console.error('Failed to load dealership logos:', err);
      setError(err.message || 'Failed to load logos.');
      toast.error('Failed to load dealership logos.');
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchLogos();
  }, [fetchLogos]);

  const handleUploadSuccess = (imageUrl, publicId) => { // publicId is also available if needed
    setNewLogoUrl(imageUrl);
    toast.success('Image uploaded successfully. Enter name and click Add Logo.');
  };

  const handleAddLogo = async () => {
    if (logos.length >= MAX_LOGOS) {
      toast.warn(`Cannot add more than ${MAX_LOGOS} logos.`);
      return;
    }
    if (!newLogoName.trim() || !newLogoUrl) {
      toast.error('Logo name and image are required.');
      return;
    }

    setIsAdding(true);
    const formData = new FormData();
    formData.append('name', newLogoName.trim());
    formData.append('logoUrl', newLogoUrl);
    formData.append('isActive', true); // New logos are active by default

    try {
      await addDealerLogo(formData);
      toast.success('Logo added successfully!');
      setNewLogoName('');
      setNewLogoUrl('');
      fetchLogos(); // Refresh list
    } catch (err) {
      console.error('Failed to add logo:', err);
      toast.error(err.message || 'Failed to add logo.');
    }
    setIsAdding(false);
  };

  const handleToggleActive = async (logo) => {
    console.log('[DealershipLogosEditor] handleToggleActive called for logo:', logo.name, 'Current isActive:', logo.isActive, 'Intended new isActive state should be:', !logo.isActive);
    const updatedLogoData = new FormData();
    updatedLogoData.append('name', logo.name);
    updatedLogoData.append('logoUrl', logo.logoUrl);
    updatedLogoData.append('isActive', !logo.isActive);

    try {
      await updateDealerLogo(logo.id, updatedLogoData);
      toast.success(`Logo '${logo.name}' status updated.`);
      fetchLogos(); // Refresh list
    } catch (err) {
      console.error('Failed to update logo status:', err);
      toast.error(err.message || 'Failed to update logo status.');
    }
  };
  
  const handleNameChange = async (logo, newName) => {
    if (!newName.trim()) {
        toast.error('Logo name cannot be empty.');
        return;
    }
    const updatedLogoData = new FormData();
    updatedLogoData.append('name', newName.trim());
    updatedLogoData.append('logoUrl', logo.logoUrl);
    updatedLogoData.append('isActive', logo.isActive);

    try {
      await updateDealerLogo(logo.id, updatedLogoData);
      toast.success(`Logo '${logo.name}' renamed to '${newName}'.`);
      fetchLogos(); // Refresh list
    } catch (err) {
      console.error('Failed to update logo name:', err);
      toast.error(err.message || 'Failed to update logo name.');
    }
  };

  const handleDeleteLogo = async (logoId, logoName) => {
    if (window.confirm(`Are you sure you want to delete the logo '${logoName}'?`)) {
      try {
        await deleteDealerLogo(logoId);
        toast.success(`Logo '${logoName}' deleted successfully.`);
        fetchLogos(); // Refresh list
      } catch (err) {
        console.error('Failed to delete logo:', err);
        toast.error(err.message || 'Failed to delete logo.');
      }
    }
  };

  if (isLoading && logos.length === 0) {
    return <CircularProgress />;
  }

  if (error && logos.length === 0) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h5" gutterBottom component="div">
        Manage Dealership Logos (Max: {MAX_LOGOS})
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {logos.length < MAX_LOGOS && (
        <Box sx={{ mb: 3, p: 2, border: '1px dashed grey', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>Add New Logo</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Logo Name"
                value={newLogoName}
                onChange={(e) => setNewLogoName(e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <CloudinaryUploadWidget 
                onUploadSuccess={handleUploadSuccess} 
                buttonText={newLogoUrl ? "Change Image" : "Upload Logo Image"}
                folder="dealership_logos" // Optional: organize in Cloudinary
              />
              {newLogoUrl && (
                <Avatar src={newLogoUrl} sx={{ width: 56, height: 56, mt: 1 }} variant="rounded" />
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddLogo}
                disabled={isAdding || !newLogoName || !newLogoUrl}
                startIcon={isAdding ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                Add Logo
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
      {logos.length >= MAX_LOGOS && (
         <Alert severity="info" sx={{ mb: 2 }}>Maximum number of logos ({MAX_LOGOS}) reached.</Alert>
      )}

      <Typography variant="h6" gutterBottom>Current Logos ({logos.length})</Typography>
      {isLoading && logos.length > 0 && <CircularProgress sx={{mb: 1}}/>}
      <List>
        {logos.map((logo) => (
          <ListItem 
            key={logo.id} 
            divider 
            secondaryAction={
              <Box>
                <Tooltip title={logo.isActive ? "Deactivate Logo" : "Activate Logo"}>
                  <Switch
                    checked={logo.isActive}
                    onChange={() => handleToggleActive(logo)}
                    color="primary"
                  />
                </Tooltip>
                <Tooltip title="Delete Logo">
                  <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteLogo(logo.id, logo.name)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            }
          >
            <ListItemAvatar>
              <Avatar src={logo.imageUrl} alt={logo.name} variant="rounded" sx={{ width: 56, height: 56, mr: 2 }} />
            </ListItemAvatar>
            {/* Allow editing name directly in the list - simple approach */}
            <TextField 
                defaultValue={logo.name} 
                variant="standard"
                onBlur={(e) => {
                    if (e.target.value !== logo.name) {
                        handleNameChange(logo, e.target.value);
                    }
                }}
                sx={{ flexGrow: 1 }}
            />
          </ListItem>
        ))}
      </List>
      {logos.length === 0 && !isLoading && (
        <Typography sx={{mt: 2}}>No dealership logos found. Add one above.</Typography>
      )}
    </Paper>
  );
};

export default DealershipLogosEditor;
